// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Arinvoices } from '@gql/base-types';
import { useGetInvoiceDetailsQuery } from '../queries/getInvoiceDetails.query.generated';
import { useGetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';

import { css } from '@emotion/css';
import { Alert, CircularProgress, Theme, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';

import { InvoiceBackLink } from '@hopdrive/feature-invoice-display';

import InvoiceDetailsContent from '@features/invoices/components/InvoiceDetailsContent';
import InvoiceDetailsContentCharges from '@features/invoices/components/InvoiceDetailsContentCharges';
import { isChargeBasedInvoiceType } from '@features/invoices/services/invoiceType';
import { SETTLED_PAYMENT_STATUSES } from '@hopdrive/sdk-ar';
import { useRefetchOnInvoiceMoneyChange } from '@features/invoices/hooks/useRefetchOnInvoiceMoneyChange';

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsProps = {
  rooftopId?: number;
  selectedInvoiceId?: number;
  /**
   * Where "back" goes when the invoice is a screen of its own rather than the
   * right half of a split. Absent while the list is visible beside it, because
   * a back link to something already on screen reads as a mistake.
   */
  backHref?: string;
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetails = ({ selectedInvoiceId, backHref }: InvoiceDetailsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  // First, fetch basic invoice info to determine invoice_type
  const { data: basicData, loading: basicLoading } = useGetInvoiceDetailsQuery({
    variables: { invoiceId: selectedInvoiceId || 0 },
    skip: !selectedInvoiceId,
  });

  const invoiceType = basicData?.arinvoices_by_pk?.invoice_type;
  const isChargeBasedInvoice = isChargeBasedInvoiceType(invoiceType);

  // Fetch charge-based data if needed. It skips Apollo's cache: on a program
  // invoice of thousands of moves the answer runs to tens of MB, and while it
  // sits in the cache every other cache write re-reads it, about 5 s each.
  // The screen refetches it instead: after its own actions, and whenever the
  // invoice's payments or credits change (useRefetchOnInvoiceMoneyChange).
  const {
    data: chargesData,
    loading: chargesLoading,
    error: chargesError,
    refetch: chargesRefetch,
  } = useGetInvoiceDetailsChargesQuery({
    variables: { invoiceId: selectedInvoiceId || 0, settledPaymentStatuses: [...SETTLED_PAYMENT_STATUSES] },
    skip: !selectedInvoiceId || !isChargeBasedInvoice,
    fetchPolicy: 'no-cache',
  });

  useRefetchOnInvoiceMoneyChange(isChargeBasedInvoice ? selectedInvoiceId : null, chargesRefetch);

  // Use legacy data if not charge-based
  const {
    data: legacyData,
    loading: legacyLoading,
    error: legacyError,
    refetch: legacyRefetch,
  } = useGetInvoiceDetailsQuery({
    variables: { invoiceId: selectedInvoiceId || 0 },
    skip: !selectedInvoiceId || isChargeBasedInvoice,
  });

  // Determine which data to use
  const loading = basicLoading || (isChargeBasedInvoice ? chargesLoading : legacyLoading);
  const error = isChargeBasedInvoice ? chargesError : legacyError;
  const refetch = isChargeBasedInvoice ? chargesRefetch : legacyRefetch;

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <div className={cls.details}>
      <div className={cls.detailsContainer}>
        {backHref ? <InvoiceBackLink component={Link} to={backHref} /> : null}

        {loading ? <CircularProgress className={cls.loading} /> : null}

        {error ? <Alert severity='error'>Error fetching invoice</Alert> : null}

        {!loading && !error ? (
          isChargeBasedInvoice ? (
            <InvoiceDetailsContentCharges
              invoice={chargesData?.arinvoices_by_pk}
              refetch={handleRefetch}
            />
          ) : (
            <InvoiceDetailsContent invoice={legacyData?.arinvoices_by_pk as unknown as Arinvoices} refetch={handleRefetch} />
          )
        ) : null}
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  details: css`
    position: relative;
  `,
  detailsContainer: css`
    position: relative;
    max-width: 1080px;
    padding: 24px;
    margin: 0 auto;
    ${theme?.breakpoints?.down('md')} {
      padding: 18px;
    }
    ${theme?.breakpoints?.down('sm')} {
      padding: 12px;
    }
  `,

  loading: css`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    margin: auto;
    color: ${theme?.palette?.primary?.main};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceDetails;
